// Очередь отправки: документы оприходования и списания, сохранённые на устройстве.
// Каждый документ сначала записывается сюда, потом уходит на сервер. Нет связи — лежит здесь и уходит сам,
// когда интернет вернётся (в том числе «Провести»). Сервер опознаёт документ по client_id — дублей не бывает.
import { api, ApiError, authStore } from '../../api/client.ts';
import { kvDel, kvGet, kvKeys, kvSet } from './store.ts';

export interface OutLine { product_id: number; cell_address: string; qty: number; price: number | null; kit_sku: string | null; vat_pct: 0 | 10 | 20; note: string | null }
export interface OutDoc {
  client_id: string; kind: 'posting' | 'write_off'; warehouse_id: number; reason: string; post: boolean;
  lines: OutLine[]; view?: any[];              // view — строки как на экране (название, артикул), чтобы продолжить без связи
  saved_at: string; number?: string; server_id?: number; error?: string | null; synced?: boolean;
}
export type SyncResult = { ok: true; doc: any } | { ok: false; offline: true } | { ok: false; offline: false; error: string; savedAsDraft?: any };

const KEY = 'outdoc:';
let cache: OutDoc[] = [];
let loaded = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
let snapshot: OutDoc[] = [];
const publish = () => { snapshot = cache.filter((d) => !d.synced); emit(); };

export const outbox = {
  subscribe(fn: () => void) { listeners.add(fn); return () => listeners.delete(fn); },
  /** документы, которые ещё не дошли до сервера */
  pending: () => snapshot,
};

async function load() {
  if (loaded) return;
  loaded = true;
  const keys = await kvKeys(KEY);
  cache = (await Promise.all(keys.map((k) => kvGet<OutDoc>(k)))).filter(Boolean) as OutDoc[];
  publish();
}
export async function outboxGet(clientId: string) { await load(); return cache.find((d) => d.client_id === clientId) ?? null; }

/** Записать документ на устройство (сразу, до любой сети) */
export async function outboxPut(doc: Omit<OutDoc, 'saved_at'>) {
  if (authStore.get().user?.role === 'viewer') throw new ApiError(403, {error:'Роль «Просмотр»: изменение данных запрещено',code:'forbidden'});
  await load();
  const prev = cache.find((d) => d.client_id === doc.client_id);
  const next: OutDoc = { ...prev, ...doc, saved_at: new Date().toISOString(), synced: false, error: null };
  cache = [...cache.filter((d) => d.client_id !== doc.client_id), next];
  await kvSet(KEY + doc.client_id, next);
  publish();
}
export async function outboxRemove(clientId: string) {
  await load();
  cache = cache.filter((d) => d.client_id !== clientId);
  await kvDel(KEY + clientId);
  publish();
}

const busy = new Map<string, Promise<SyncResult>>();
/** Отправить один документ. Сохранение и проведение — одним запросом; повтор безопасен */
export function syncOne(clientId: string): Promise<SyncResult> {
  const running = busy.get(clientId);
  if (running) return running.then(() => syncOne(clientId));
  const p = (async (): Promise<SyncResult> => {
    await load();
    const d = cache.find((x) => x.client_id === clientId);
    if (!d) return { ok: false, offline: false, error: 'Документ не найден на устройстве' };
    const sentAt = d.saved_at;
    const send = (post: boolean) => api<any>(`/stock-docs/by-client/${d.client_id}`, { method: 'PUT',
      body: { warehouse_id: d.warehouse_id, kind: d.kind, reason: d.reason, lines: d.lines, post } });
    const done = async (doc: any) => {
      const cur = cache.find((x) => x.client_id === clientId);
      if (!cur) return;
      // пока шёл запрос, документ могли изменить — тогда он остаётся в очереди и уйдёт следующим заходом
      if (cur.saved_at === sentAt) { cache = cache.filter((x) => x.client_id !== clientId); await kvDel(KEY + clientId); }
      else { cur.number = doc.number; cur.server_id = doc.id; await kvSet(KEY + clientId, cur); }
      publish();
    };
    try {
      const doc = await send(d.post);
      await done(doc);
      return { ok: true, doc };
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 0 || e.status >= 500 || e.status === 401)) return { ok: false, offline: true };
      const error = String(e?.message ?? e);
      // провести не получилось (например, списание больше остатка) — сохраняем хотя бы черновиком на сервере
      if (d.post) {
        try {
          const draft = await send(false);
          await done(draft);
          return { ok: false, offline: false, error, savedAsDraft: draft };
        } catch (e2: any) {
          if (e2 instanceof ApiError && (e2.status === 0 || e2.status >= 500)) return { ok: false, offline: true };
        }
      }
      const cur = cache.find((x) => x.client_id === clientId);
      if (cur) { cur.error = error; await kvSet(KEY + clientId, cur); publish(); }
      return { ok: false, offline: false, error };
    }
  })().finally(() => busy.delete(clientId));
  busy.set(clientId, p);
  return p;
}

let syncing = false;
/** Отправить всё, что ждёт. Вызывается при появлении связи и по таймеру */
export async function syncAll(onResult?: (d: OutDoc, r: SyncResult) => void) {
  if (syncing) return;
  syncing = true;
  try {
    await load();
    for (const d of [...cache].sort((a, b) => a.saved_at.localeCompare(b.saved_at))) {
      if (d.error) continue;                    // ждёт решения человека, сам не повторяется
      const r = await syncOne(d.client_id);
      if (!r.ok && r.offline) break;            // связи всё ещё нет — остальные тоже подождут
      onResult?.(d, r);
    }
  } finally { syncing = false; }
}
export async function outboxInit() { await load(); }
export const newClientId = () => (crypto.randomUUID ? crypto.randomUUID()
  : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16)));
